import { createStore, createEffect, createEvent, sample, attach } from 'effector';
import { USER_VARIABLE, axiosClient, buildQuery } from '@nx-frontend/shared/api';
import { postPortalContentClientApi } from '@nx-frontend/shared/api';
import { getPortalPages } from '@nx-frontend/shared/api/webnotes/client';
import { $menuState, updateMenuItemEvent } from '@nx-frontend/shared/store/feature-menu-tree';
import { $portalSettings } from '@nx-frontend/shared/store/portal';
import { $user } from '@nx-frontend/shared/store/user';
import {
  NoteMenuItem,
  MenuType,
  NoteDto,
  MenuItem,
  NotesStoreNote,
  AddNoteEventPayload,
  UpdateNoteEventPayload,
} from '@nx-frontend/shared/types';
import { GetNoteRange, GetNotesResult, NoteCounterType } from '@nx-frontend/shared/types';
import { MenusState } from '@nx-frontend/shared/types';
import { ChangeEventType } from '@nx-frontend/shared/types';
import { positionTypeToMenuType } from '@nx-frontend/shared/utils';

export type NoteType = 'note' | 'folder';
export interface WorkspaceNotesFetchParams {
  total: number;
  offset: number;
  isFetching: boolean;
  isLoaded: boolean;
}

export interface WorkspaceNotes {
  entities: Record<string, NotesStoreNote>;
  fetch: Record<string, WorkspaceNotesFetchParams>;
}

export type SortOrder = 'ASC' | 'DESC';
export type NotesSortBy = 'title' | 'updatedAt' | 'createdAt';

export interface NotesState {
  workspaces: Record<string, WorkspaceNotes>;
  sortBy: NotesSortBy;
  sortOrder: SortOrder;
}

export const initialNotesState: NotesState = {
  workspaces: {},
  sortBy: 'title',
  sortOrder: 'ASC',
};

export interface FetchNotesParams {
  workspaceId: string;
  type: 'note' | 'folder';
  globalId?: string;
  range?: GetNoteRange;
  parentId?: string | string[];
  rootId?: string;
  fetchPortalsNotes?: boolean;
  counters?: boolean;
  countersTypes?: NoteCounterType[];
  abortController?: AbortController;
  order?: ['title' | 'updatedAt' | 'createdAt', 'ASC' | 'DESC'];
}

export interface FetchNoteEventPayload {
  workspaceId: string;
  globalId: string;
  portalMenuItem?: NoteMenuItem | undefined;
}

export const $notes = createStore<NotesState>(initialNotesState);

export const setNotesSortByEvent = createEvent<NotesSortBy>();
export const setNotesSortOrderEvent = createEvent<SortOrder>();

export const fetchNotesEvent = createEvent<FetchNotesParams>();
export const startFetchNotesEvent = createEvent<FetchNotesParams>();
export const addNoteEvent = createEvent<AddNoteEventPayload>();
export const updateNoteEvent = createEvent<UpdateNoteEventPayload>();
export const deleteNoteEvent = createEvent<UpdateNoteEventPayload>();
export const fetchNoteEvent = createEvent<FetchNoteEventPayload>();
export const addNoteToPortalEvent = createEvent<{
  workspaceId: string;
  globalId: string;
  showMakeInvisibleNotification?: boolean;
}>();
export const removeNoteFromPortalEvent = createEvent<{
  workspaceId: string;
  globalId: string;
}>();
export const resetFetchEvent = createEvent<{ workspaceId: string; fetchId: string }>();

/**
 * call this event when you want to change not visibility in portal
 * it also accepts triggerer param, which is used to determine what part of the app triggered this event
 */
export const changeNoteVisibilityEvent = createEvent<{
  workspaceId: string;
  globalId: string;
  visible: boolean;
  triggerer?: string;
}>();

export const fetchWorkspaceFoldersEvent = createEvent<FetchNotesParams>();

export const getNotes = async ({
  workspaceId,
  globalId,
  type,
  range,
  parentId,
  rootId,
  fetchPortalsNotes,
  counters,
  countersTypes,
  abortController,
  order,
}: FetchNotesParams) => {
  const queryParams = buildQuery({
    filter: {
      ...(type ? { type } : {}),
      ...(globalId ? { globalId } : {}),
      ...(parentId ? { parentId } : {}),
      ...(fetchPortalsNotes !== undefined ? { is_portal_share: fetchPortalsNotes } : {}),
    },
    ...(range ? { range } : {}),
    ...(rootId ? { rootId } : {}),
    ...(counters ? { counters } : {}),
    ...(countersTypes ? { countersTypes: countersTypes.join(',') } : {}),
    ...(order ? { order } : {}),
  });
  const query = queryParams ? `?${queryParams}` : '';
  const {
    data: { items, total },
  } = await axiosClient.get<GetNotesResult>(`/v2/api/workspaces/${workspaceId}/notes${query}`, {
    ...(abortController ? { signal: abortController.signal } : {}),
  });
  return { workspaceId, notes: items, total, type, parentId };
};

export const fetchNotesFx = createEffect(getNotes);

$notes.on(startFetchNotesEvent, (_, params: FetchNotesParams) => {
  const { workspaceId, type, parentId } = params;

  const workspace = _.workspaces[workspaceId] || { entities: {}, fetch: {} };
  const fetchKey = `${type}-${parentId || 'all'}`;
  const workpsaceFetch = workspace.fetch || {};

  return {
    ..._,
    workspaces: {
      ..._.workspaces,
      [workspaceId]: {
        ...workspace,
        fetch: {
          ...workspace.fetch,
          [fetchKey]: {
            ...(workpsaceFetch[fetchKey] || {}),
            isFetching: true,
          },
        },
      },
    },
  };
});

/**
 * When fetchNotesEvent will called, it will triggerd:
 * - startFetchNotesEvent — for set isFetching to true
 * - fetchNotesFx — for fetch notes from server
 */
sample({
  source: $notes,
  clock: fetchNotesEvent,
  filter: (_, params) => {
    const { workspaceId, type, parentId } = params;
    const workspace = _.workspaces[workspaceId] || { entities: {}, fetch: {} };
    const workspaceFetch = workspace.fetch || {};
    const fetchKey = `${type}-${parentId || 'all'}`;
    const { isFetching, isLoaded } = workspaceFetch[fetchKey] || {};
    // if not fetching yet or is not loaded, it will trigger fetchNotesFx effect
    return !(isFetching || isLoaded);
  },
  fn: (_, params) => {
    return params;
  },
  target: [startFetchNotesEvent, fetchNotesFx],
});

$notes.on(fetchNotesFx.doneData, (_, { workspaceId, notes, total, type, parentId }) => {
  const itemsMap: Record<string, NotesStoreNote> = {};
  notes.forEach((note) => {
    itemsMap[note.globalId] = note;
  });

  const workspace = _.workspaces[workspaceId] || { entities: {}, fetch: {} };
  const entities = { ...(workspace?.entities || {}), ...itemsMap };
  const offset = Object.values(entities).filter((item) => item.type === type).length;
  const fetchKey = `${type}-${parentId || 'all'}`;

  return {
    ..._,
    workspaces: {
      ..._.workspaces,
      [workspaceId]: {
        ...workspace,
        entities,
        fetch: {
          ...workspace.fetch,
          [fetchKey]: {
            total,
            offset,
            isFetching: false,
            isLoaded: notes.length === 0 || offset >= total,
          },
        },
      },
    },
  };
});

$notes.on(addNoteEvent, (store, payload) => {
  if (!payload.workspaceId || !payload.globalId) return store;
  const workspace = store.workspaces[payload.workspaceId] || {};
  const entities = workspace.entities || {};
  return {
    ...store,
    workspaces: {
      ...store.workspaces,
      [payload.workspaceId]: {
        ...workspace,
        entities: {
          ...entities,
          [payload.globalId]: {
            ...payload,
          },
        },
      },
    },
  };
});

$notes.on(updateNoteEvent, (store, payload) => {
  if (!payload.workspaceId || !payload.globalId) return store;

  const workspace = store.workspaces[payload.workspaceId] || {};
  const entities = workspace.entities || {};

  /**
   * If note is folder and it was moved to trash, need to mark all children as inTrash for not show in all pages
   */
  const updatedItem: NotesStoreNote = store.workspaces[payload.workspaceId]?.entities[payload.globalId];
  if (updatedItem?.type === 'folder' && payload.parentId === 'trash') {
    const updated: string[] = [];
    // Recursive function for update all children
    const getUpdateItem = (items: NotesStoreNote[], parentId: string, updated: string[]) => {
      const notes = items.filter((item) => item.parentId === parentId);
      notes.forEach((note) => {
        if (note.type === 'folder') {
          getUpdateItem(items, note.globalId, updated);
        }
        updated.push(note.globalId);
      });
    };
    const notes = Object.values(store.workspaces[payload.workspaceId]?.entities ?? {}) || [];
    getUpdateItem(notes, payload.globalId, updated);
    updated.forEach((globalId) => {
      entities[globalId] = {
        ...entities[globalId],
        inTrash: true,
      };
    });
  }

  return {
    ...store,
    workspaces: {
      [payload.workspaceId]: {
        ...workspace,
        entities: {
          ...entities,
          [payload.globalId]: {
            ...entities[payload.globalId],
            ...payload,
          },
        },
      },
    },
  };
});

$notes.on(deleteNoteEvent, (store, payload) => {
  const workspace = store.workspaces[payload.workspaceId] || {};
  const entities = workspace.entities || {};

  delete entities[payload.globalId];

  return {
    ...store,
    workspaces: {
      [payload.workspaceId]: {
        ...workspace,
        entities,
      },
    },
  };
});

/**
 * Fetching single page by globalId
 */
export const fetchNoteFx = createEffect(async ({ workspaceId, globalId, portalMenuItem }: FetchNoteEventPayload) => {
  const response = await getNotes({ workspaceId, globalId, type: 'note' });
  const note = response.notes[0];
  if (portalMenuItem && note) {
    note.portalMenuItem = portalMenuItem;
  }
  return { note: response.notes[0], workspaceId, globalId };
});

$notes.on(fetchNoteEvent, (store, payload) => {
  const workspace = store.workspaces[payload.workspaceId] || {};
  const entities = workspace.entities || {};
  const fetch = workspace.fetch || {};

  return {
    ...store,
    workspaces: {
      [payload.workspaceId]: {
        ...workspace,
        entities: {
          ...entities,
          [payload.globalId]: {
            ...entities[payload.globalId],
            isFetching: entities[payload.globalId] === undefined,
          },
        },
        fetch,
      },
    },
  };
});

$notes.on(
  fetchNoteFx.doneData,
  (store, payload: { note: NoteDto | undefined; workspaceId: string; globalId: string }) => {
    const workspace = store.workspaces[payload.workspaceId] || {};
    let entities = workspace.entities || {};
    const fetch = workspace.fetch || {};

    if (payload.note) {
      entities = {
        ...entities,
        [payload.globalId]: {
          ...payload.note,
          isFetching: false,
        },
      };
    } else {
      delete entities[payload.globalId];
    }

    return {
      ...store,
      workspaces: {
        [payload.workspaceId]: {
          ...workspace,
          entities,
          fetch,
        },
      },
    };
  }
);

/**
 * When fetchNoteEvent will triggered, then will check is note exists in store,
 * in case if not exists, will trigger fetchNoteFx effect
 */
sample({
  source: $notes,
  clock: fetchNoteEvent,
  filter: (store, payload) => {
    const workspace = store.workspaces[payload.workspaceId] || {};
    const entities = workspace.entities || {};
    return entities[payload.globalId]?.isFetching === true;
  },
  fn: (_, params) => params,
  target: fetchNoteFx,
});

$notes.on(addNoteToPortalEvent, (store, params) => {
  const workspace = store.workspaces[params.workspaceId] || {};
  const showMakeInvisibleNotification = params.showMakeInvisibleNotification;
  const entities = workspace.entities || {};

  entities[params.globalId] = {
    ...entities[params.globalId],
    showMakeInvisibleNotification: undefined === showMakeInvisibleNotification ? true : showMakeInvisibleNotification,
    isSharedForPortal: true,
  };
  return {
    ...store,
    workspaces: {
      ...store.workspaces,
      [params.workspaceId]: {
        ...workspace,
        entities,
      },
    },
  };
});
$notes.on(removeNoteFromPortalEvent, (store, params) => {
  const workspace = store.workspaces[params.workspaceId] || {};
  const entities = workspace.entities || {};

  entities[params.globalId] = {
    ...entities[params.globalId],
    isSharedForPortal: false,
  };
  return {
    ...store,
    workspaces: {
      ...store.workspaces,
      [params.workspaceId]: {
        ...workspace,
        entities,
      },
    },
  };
});

/**
 * changes visiblity of note in portal, gets new visibility value from item.visible field
 */
export const changeNoteVisibilityInPortalFx = createEffect(
  async (params: {
    workspaceId: string;
    globalId: string;
    portalId: string;
    userId: number;
    item: NoteMenuItem;
    triggerer?: string;
  }) => {
    const { workspaceId, globalId, portalId, userId, item: menuItem } = params;
    const visible = menuItem.visible;
    try {
      const [page] = await getPortalPages({ workspaceId, noteId: globalId });
      const pageId = page.id;
      await postPortalContentClientApi({
        changeEventsDto: [
          {
            type: 'changePageVisibility',
            newValue: {
              id: pageId,
              visible,
            },
            oldValue: {
              id: pageId,
              visible: !visible,
            },
            meta: {
              pageId,
              userId,
            },
          },
          {
            type: ChangeEventType.CHANGE_MENU_ITEM_VISIBILITY,
            newValue: {
              id: menuItem.id,
              visible,
            },
            oldValue: {
              id: menuItem.id,
              visible: !visible,
            },
            meta: {
              pageId,
              userId,
            },
          },
        ],
        workspaceId,
        portalId,
      });
    } catch (err) {
      // TODO: should handle error
      console.error(err);
    }

    return params;
  }
);

const startChangeNoteVisibilityEvent = createEvent<{
  workspaceId: string;
  globalId: string;
  item: { visible: boolean };
}>();

const findPortalMenuItemByTargetId = (menuState: MenusState, targetId: string): MenuItem | undefined => {
  let menuItem: MenuItem | undefined = undefined;
  [MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_TOPBAR, MenuType.PORTAL_FOOTER].forEach((menuType) => {
    const item = Object.values(menuState[menuType].entities).find((item) => item.targetId === targetId);
    if (!item) return;
    menuItem = item;
  });
  return menuItem;
};

sample({
  source: {
    user: $user,
    menuState: $menuState,
    portalSettings: $portalSettings,
  },
  clock: changeNoteVisibilityEvent,
  fn: (_, params) => {
    const { user, menuState, portalSettings } = _;

    const menuItem = findPortalMenuItemByTargetId(menuState, params.globalId);
    const menuType = menuItem?.positionType ? positionTypeToMenuType(menuItem.positionType) : MenuType.PORTAL_SIDEBAR;

    return {
      ...params,
      userId: user.id,
      portalId: portalSettings.id,
      menuType,
      item: {
        ...menuItem,
        visible: params.visible,
      },
      triggerer: params.triggerer,
    };
  },
  target: [startChangeNoteVisibilityEvent, changeNoteVisibilityInPortalFx, updateMenuItemEvent],
});

$notes.on(startChangeNoteVisibilityEvent, (store, params) => {
  const workspace = store.workspaces[params.workspaceId] || {};
  const entities = workspace.entities || {};

  entities[params.globalId] = {
    ...entities[params.globalId],
    isMakingInvisible: params.item.visible === false,
  };

  return {
    ...store,
    workspaces: {
      ...store.workspaces,
      [params.workspaceId]: {
        ...workspace,
        entities,
      },
    },
  };
});

$notes.on(changeNoteVisibilityInPortalFx.doneData, (store, params) => {
  const workspace = store.workspaces[params.workspaceId] || {};
  const entities = workspace.entities || {};

  entities[params.globalId] = {
    ...entities[params.globalId],
    isMakingInvisible: false,
  };

  return {
    ...store,
    workspaces: {
      ...store.workspaces,
      [params.workspaceId]: {
        ...workspace,
        entities,
      },
    },
  };
});

export const getNoteOriginalFx = createEffect(
  async (params: UpdateNoteEventPayload): Promise<UpdateNoteEventPayload> => {
    return params;
  }
);

export const getNotesNoteFx = attach({
  effect: getNoteOriginalFx,
  source: $notes,
  mapParams: (params: UpdateNoteEventPayload, store) => {
    const workspace = store.workspaces[params.workspaceId] || {};
    const entities = workspace.entities || {};
    return entities[params.globalId];
  },
});

export function generateNote(params: {
  globalId: string;
  workspaceId: string;
  title: string;
  parentId: string;
  userId: number;
}): NoteDto {
  const { globalId, workspaceId, title, parentId, userId } = params;
  const createdAt = Math.floor(Date.now() / 1000);
  return {
    globalId,
    parentId,
    createdAt,
    dateAdded: createdAt,
    dateUpdated: createdAt,
    updatedAt: createdAt,
    type: 'note',
    role: 'note',
    title,
    url: '',
    locationLat: 0,
    locationLng: 0,
    shared: false,
    favorite: false,
    lastChangeBy: userId,
    cntNotes: 0,
    size: 0,
    editnote: true,
    isEncrypted: false,
    isCompleted: false,
    workspaceId,
    isImported: false,
    isFullwidth: false,
    userId,
    isReady: true,
    outliner: false,
  };
}

/**
 * Fetch all workspace folders if not fetched yet
 */
sample({
  source: $notes,
  clock: fetchWorkspaceFoldersEvent,
  filter: (_, params) => {
    const workpsaceFetch = _.workspaces[params.workspaceId]?.fetch || {};
    return !workpsaceFetch['folder-all']?.isLoaded;
  },
  fn: (_, params) => {
    return { ...params };
  },
  target: fetchNotesFx,
});

$notes.on(setNotesSortByEvent, (store, sortBy) => {
  return {
    ...store,
    sortBy,
  };
});

$notes.on(setNotesSortOrderEvent, (store, sortOrder) => {
  return {
    ...store,
    sortOrder,
  };
});

let debounceID: NodeJS.Timeout | null | number = null;
const setNotesSortFx = createEffect(async (params: { sortBy: NotesSortBy; sortOrder: SortOrder; userId: number }) => {
  const { sortBy, sortOrder, userId } = params;

  debounceID && clearTimeout(debounceID);
  debounceID = setTimeout(async () => {
    await axiosClient.post(
      `/v2/api/users/vars/${USER_VARIABLE.NOTES_SORT}`,
      {
        value: { sortBy, sortOrder },
      },
      { userId }
    );
  }, 500);
});

sample({
  source: { user: $user, notes: $notes },
  clock: [setNotesSortByEvent, setNotesSortOrderEvent],
  fn: (params) => {
    const { user, notes } = params;
    const { sortBy, sortOrder } = notes;
    return { sortBy, sortOrder, userId: user.id };
  },
  target: setNotesSortFx,
});

$notes.on(resetFetchEvent, (store, params) => {
  const workspace = store.workspaces[params.workspaceId] || {};
  const workspaceFetch = workspace.fetch || {};
  if (workspaceFetch[params.fetchId] && workspaceFetch[params.fetchId].isLoaded) {
    return store;
  }
  const [, parentId] = params.fetchId.split('-');

  const entities = workspace.entities || {};
  const folders = Object.values(entities).filter((item) => {
    const type = item.type;
    return type === 'folder' || (type === 'note' && item.parentId !== parentId && parentId !== 'all');
  });
  const foldersEntites: Record<string, NotesStoreNote> = {};
  folders.forEach((folder) => {
    foldersEntites[folder.globalId] = folder;
  });

  return {
    ...store,
    workspaces: {
      ...store.workspaces,
      [params.workspaceId]: {
        ...workspace,
        entities: foldersEntites,
        fetch: {
          ...workspace.fetch,
          [params.fetchId]: {
            ...workspace.fetch[params.fetchId],
            isFetching: false,
            offset: 0,
          },
        },
      },
    },
  };
});
